import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { AutocompleteAddressField, Button, CurrencyField, DateField, SelectField, TextField } from '@roc/ui';
import { format as formatDate, isValid } from 'date-fns';
import { useStore } from '@roc/client-portal-shared/hooks';
import { observer } from 'mobx-react';
import { useState } from 'react';
import {
  Comparable,
  COMPARABLE_CONDITION_OPTIONS,
  ValuationDisputeFormData,
  ValuationDisputeStore,
} from '../../../stores/documentForms/valuationDisputeStore';

const STEPS = ['Property Info', 'Purpose', 'Comparables', 'Comments'];

const DATE_FORMAT = 'MM/dd/yyyy';

const COMPARABLE_ROWS: Array<{
  label: string;
  field: keyof Comparable;
  type: 'text' | 'date' | 'select';
}> = [
    { label: 'Address of Comp', field: 'address', type: 'text' },
    { label: 'City, State, Zip', field: 'cityStateZip', type: 'text' },
    { label: 'Closed / Lease Date', field: 'closedLeaseDate', type: 'date' },
    { label: 'MLS # / Data Source', field: 'mlsDataSource', type: 'text' },
    { label: 'Sales Price / Rent Amount', field: 'salesPriceRentAmount', type: 'text' },
    { label: 'Prox. to Subject', field: 'proxToSubject', type: 'text' },
    { label: 'Above Grade Sq Ft', field: 'aboveGradeSqFt', type: 'text' },
    { label: 'Below Grade Sq Ft', field: 'belowGradeSqFt', type: 'text' },
    { label: 'Age', field: 'age', type: 'text' },
    { label: 'Property Condition', field: 'propertyCondition', type: 'select' },
    { label: 'Bed / Bath Count', field: 'bedBathCount', type: 'text' },
    { label: 'Lot Size', field: 'lotSize', type: 'text' },
    { label: 'Special Amenities', field: 'specialAmenities', type: 'text' },
  ];

const useStyles = makeStyles(theme => ({
  stepContent: {
    paddingTop: theme.spacing(3),
  },
  sectionSubtitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  checkboxOption: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
    '&.selected': {
      borderColor: theme.palette.primary.main,
      background: '#e0f7f9',
    },
  },
  checkboxSubtext: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    display: 'block',
    lineHeight: 1.5,
  },
  elaboration: {
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(4),
    paddingBottom: theme.spacing(1),
  },
  tableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflowX: 'auto',
  },
  tableHeaderCell: {
    fontWeight: 600,
    fontSize: '0.8rem',
    background: '#f7f7f7',
    whiteSpace: 'nowrap',
    minWidth: 140,
  },
  tableRowLabelCell: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#444',
    background: '#fafafa',
    whiteSpace: 'nowrap',
    minWidth: 170,
  },
  infoBox: {
    background: '#f0fbfc',
    border: '1px solid #d0f0ec',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    '& li': {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.7,
    },
  },
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  stepCount: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
}));

interface Props {
  store: ValuationDisputeStore;
  taskId: number;
  loanId: number;
  propertyId: number;
  taskName: string;
  onClose: () => void;
}

type Step1Errors = {
  propertyAddress?: string;
  effectiveDate?: string;
  appraisedValue?: string;
  internalValue?: string;
};

const validateStep1 = (formData: any): Step1Errors => {
  const errors: Step1Errors = {};
  if (!formData.propertyAddress?.trim()) errors.propertyAddress = 'Required';
  if (!formData.effectiveDate) errors.effectiveDate = 'Required';
  return errors;
};

export const ValuationDisputeForm = observer(
  ({ store, taskId, loanId, propertyId, taskName, onClose }: Props) => {
    const classes = useStyles();
    const { currentStep, isSaving } = store;
    const [step1Errors, setStep1Errors] = useState<Step1Errors>({});

    const handleNext = () => {
      if (currentStep === 0) {
        const errors = validateStep1(store.formData);
        setStep1Errors(errors);
        if (Object.keys(errors).length > 0) return;
      }
      if (currentStep < STEPS.length - 1) {
        store.setCurrentStep(currentStep + 1);
      } else {
        store.saveForm(taskId, loanId, propertyId, taskName, onClose);
      }
    };

    return (
      <Box display="flex" flexDirection="column" style={{ minHeight: 460, width: '100%' }}>
        <Stepper activeStep={currentStep} alternativeLabel>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box className={classes.stepContent}>
          {currentStep === 0 && <Step1PropertyInfo store={store} errors={step1Errors} onFieldChange={() => setStep1Errors({})} />}
          {currentStep === 1 && <Step2Purpose store={store} />}
          {currentStep === 2 && <Step3Comparables store={store} />}
          {currentStep === 3 && <Step4Comments store={store} />}
        </Box>

        <Box className={classes.navRow}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => store.setCurrentStep(currentStep - 1)}
            testId="back"
            disabled={isSaving}
            style={{ visibility: currentStep === 0 ? 'hidden' : 'visible' }}
          >
            Back
          </Button>
          <Typography className={classes.stepCount}>
            Step {currentStep + 1} of {STEPS.length}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            testId="next"
            disabled={isSaving}
          >
            {currentStep === STEPS.length - 1 ? 'Submit' : 'Next'}
          </Button>
        </Box>
      </Box>
    );
  }
);

const toDateOrNull = (value: string): Date | null => {
  if (!value) return null;
  const parts = value.split('-').map(Number);
  if (parts.length === 3 && parts.every(n => !isNaN(n))) {
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const fromDate = (date: Date | null): string =>
  date && isValid(date) ? formatDate(date, 'yyyy-MM-dd') : '';

const FieldError = ({ message }: { message?: string }) =>
  message ? (
    <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 4, marginLeft: 14 }}>
      {message}
    </Typography>
  ) : null;

const Step1PropertyInfo = observer(({
  store,
  errors = {},
  onFieldChange,
}: {
  store: ValuationDisputeStore;
  errors?: Step1Errors;
  onFieldChange?: () => void;
}) => {
  const { formData } = store;
  const change = (field: keyof ValuationDisputeFormData, value: any) => {
    store.setField(field, value);
    onFieldChange?.();
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <AutocompleteAddressField
          label="Property Address *"
          value={formData.propertyAddress}
          onChange={value => change('propertyAddress', value)}
          fullWidth
          testId="propertyAddress"
          standaloneLabel={false}
        />
        <FieldError message={errors.propertyAddress} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DateField
          label="Effective Date of the Appraisal Report *"
          value={toDateOrNull(formData.effectiveDate)}
          onChange={date => change('effectiveDate', fromDate(date))}
          inputVariant="outlined"
          format={DATE_FORMAT}
          fullWidth
          testId="effectiveDate"
          error={!!errors.effectiveDate}
          helperText={errors.effectiveDate}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <CurrencyField
          label="Appraised Value"
          value={formData.appraisedValue}
          onChange={(_, value) => change('appraisedValue', String(value))}
          variant="outlined"
          fullWidth
          testId="appraisedValue"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <CurrencyField
          label="Internal Value"
          value={formData.internalValue}
          onChange={(_, value) => change('internalValue', String(value))}
          variant="outlined"
          fullWidth
          testId="internalValue"
        />
      </Grid>
    </Grid>
  );
});

const Step2Purpose = observer(({ store }: { store: ValuationDisputeStore }) => {
  const classes = useStyles();
  const { formData } = store;

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography className={classes.sectionSubtitle}>
          Select all that apply and provide supporting details
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Box
          className={`${classes.checkboxOption} ${formData.salesNotRepresentative ? 'selected' : ''}`}
          onClick={() => store.setField('salesNotRepresentative', !formData.salesNotRepresentative)}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.salesNotRepresentative}
                onChange={e => store.setField('salesNotRepresentative', e.target.checked)}
                onClick={e => e.stopPropagation()}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  <strong>Sales not representative</strong>
                </Typography>
                <Typography className={classes.checkboxSubtext}>
                  The sales used in the appraisal report are not representative of the subject
                  property or market area. Please use the Alternate Comparable Sales grid in the
                  next step.
                </Typography>
              </Box>
            }
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box
          className={`${classes.checkboxOption} ${formData.incorrectPropertyInformation ? 'selected' : ''}`}
          onClick={() =>
            store.setField('incorrectPropertyInformation', !formData.incorrectPropertyInformation)
          }
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.incorrectPropertyInformation}
                onChange={e =>
                  store.setField('incorrectPropertyInformation', e.target.checked)
                }
                onClick={e => e.stopPropagation()}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  <strong>Incorrect property information</strong>
                </Typography>
                <Typography className={classes.checkboxSubtext}>
                  The subject property information is incorrect. Please elaborate below.
                </Typography>
              </Box>
            }
          />
        </Box>
        {formData.incorrectPropertyInformation && (
          <Box className={classes.elaboration}>
            <TextField
              label="Please elaborate on the incorrect information"
              value={formData.incorrectPropertyInformationDetails}
              onChange={e =>
                store.setField('incorrectPropertyInformationDetails', e.target.value)
              }
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              testId="incorrectPropertyInformationDetails"
            />
          </Box>
        )}
      </Grid>

      <Grid item xs={12}>
        <Box
          className={`${classes.checkboxOption} ${formData.other ? 'selected' : ''}`}
          onClick={() => store.setField('other', !formData.other)}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.other}
                onChange={e => store.setField('other', e.target.checked)}
                onClick={e => e.stopPropagation()}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  <strong>Other</strong>
                </Typography>
                <Typography className={classes.checkboxSubtext}>
                  There is another reason for this appeal. Please elaborate below.
                </Typography>
              </Box>
            }
          />
        </Box>
        {formData.other && (
          <Box className={classes.elaboration}>
            <TextField
              label="Please elaborate"
              value={formData.otherDetails}
              onChange={e => store.setField('otherDetails', e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              testId="otherDetails"
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );
});

const ComparableCell = observer(
  ({
    column,
    row,
    store,
  }: {
    column: 'subject' | number;
    row: typeof COMPARABLE_ROWS[number];
    store: ValuationDisputeStore;
  }) => {
    const value =
      column === 'subject'
        ? store.formData.subject[row.field]
        : store.formData.comparables[column as number][row.field];

    const onChange = (val: string) => store.setComparableField(column, row.field, val);
    const colKey = column === 'subject' ? 'subject' : `comp${column}`;

    if (row.type === 'date') {
      return (
        <DateField
          value={toDateOrNull(value)}
          onChange={date => onChange(fromDate(date))}
          inputVariant="outlined"
          format={DATE_FORMAT}
          fullWidth
          testId={`${colKey}-${row.field}`}
        />
      );
    }
    if (row.type === 'select') {
      return (
        <SelectField
          value={value}
          onChange={onChange}
          options={COMPARABLE_CONDITION_OPTIONS}
          variant="outlined"
          fullWidth
          testId={`${colKey}-${row.field}`}
        />
      );
    }
    return (
      <TextField
        value={value}
        onChange={e => onChange(e.target.value)}
        variant="outlined"
        fullWidth
        testId={`${colKey}-${row.field}`}
      />
    );
  }
);

const Step3Comparables = observer(({ store }: { store: ValuationDisputeStore }) => {
  const classes = useStyles();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box className={classes.infoBox}>
          <ul>
            <li>Comparables must be different from those on the appraisal and listed on MLS.</li>
            <li>
              Sales comparables should be closed sales within the last 180 days. Active or pending
              sales cannot be considered.
            </li>
            <li>
              Rental comparables should not be active listings, seasonal, or short term.
            </li>
            <li>Comparables must be similar in GLA, Age, Design, Amenities, and site area.</li>
            <li>
              Comparables should be drawn from within the same neighborhood. Avoid sales less
              proximate than those in the report.
            </li>
            <li>Comparables must be an arms-length transaction.</li>
          </ul>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell}>Field</TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  Subject
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  Comparable #1
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  Comparable #2
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  Comparable #3
                </TableCell>
                <TableCell className={classes.tableHeaderCell} align="center">
                  Comparable #4
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {COMPARABLE_ROWS.map(row => (
                <TableRow key={row.field}>
                  <TableCell className={classes.tableRowLabelCell}>{row.label}</TableCell>
                  <TableCell>
                    <ComparableCell column="subject" row={row} store={store} />
                  </TableCell>
                  {[0, 1, 2, 3].map(i => (
                    <TableCell key={i}>
                      <ComparableCell column={i} row={row} store={store} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
});

const Step4Comments = observer(({ store }: { store: ValuationDisputeStore }) => {
  const { formData } = store;
  const { globalStore } = useStore();
  const isInternalUser = globalStore.isInternalPortal;
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert severity="warning">
          <strong>Please note:</strong> This is not a guarantee that there will be any change in
          the appraiser&apos;s opinion. In accordance with the Appraiser Independence Rule (AIR),
          do not provide any anticipated, estimated, or desired value for the subject or expected
          loan amount.
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Client Comments"
          value={formData.clientComments}
          onChange={e => store.setField('clientComments', e.target.value)}
          variant="outlined"
          fullWidth
          multiline
          minRows={4}
          testId="clientComments"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Reviewer Comments"
          value={formData.reviewerComments}
          onChange={e => store.setField('reviewerComments', e.target.value)}
          variant="outlined"
          fullWidth
          multiline
          minRows={4}
          disabled={!isInternalUser}
          testId="reviewerComments"
        />
      </Grid>
    </Grid>
  );
});
